import React, { useRef, useEffect } from "react";
import { PlxsTextField } from "@plexlogic/plexus-ui";
import { DrawData } from "../../types/DrawData";
import { WizardStepHeader } from "../index";
import WizardFooterNav from "../WizardFooterNav";
import TurnstileWidget from "../TurnstileWidget";
import {
  hasContent,
  isValidDrawName,
  MIN_DRAW_NAME_LENGTH,
} from "../../utils/validationUtils";
import { scrollToFirstError } from "../../utils/scrollToFirstError";
import { apiService } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import config from "../../config";

interface DrawSetupStepProps {
  drawData: DrawData;
  updateDrawData: (updates: Partial<DrawData>) => void;
  onNext: () => void;
  onDrawCreated: (drawId: string, drawNo?: string) => void;
  isLocked?: boolean;
}

const DrawSetupStep: React.FC<DrawSetupStepProps> = ({
  drawData,
  updateDrawData,
  onNext,
  onDrawCreated,
  isLocked = false,
}) => {
  const { isAuthenticated } = useAuth();
  const isMountedRef = useRef(true);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [showErrors, setShowErrors] = React.useState(false);
  const [turnstileToken, setTurnstileToken] = React.useState<string | null>(
    null
  );
  const [turnstileResetKey, setTurnstileResetKey] = React.useState(0);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleNext = async () => {
    if (isLocked) {
      onNext();
      return;
    }

    // Show errors if form is invalid
    if (!isFormValid) {
      setShowErrors(true);
      scrollToFirstError([
        { hasError: !isValidDrawName(drawData.drawName), testId: "draw-name-input" },
        { hasError: !hasContent(drawData.organisation), testId: "organisation-input" },
      ]);
      return;
    }

    if (isSubmitting) return;

    const canCreateNow =
      isAuthenticated || config.features.anonymousDraftsEnabled;
    const isCreate = !drawData.drawId && canCreateNow;
    const isRename = !isCreate && !!drawData.drawId;
    if (isCreate || isRename) {
      setIsSubmitting(true);
      try {
        if (isCreate) {
          const response = await apiService.createDraw({
            draw_name: drawData.drawName,
            organisation: drawData.organisation,
            turnstile_token: turnstileToken || undefined,
          });

          updateDrawData({
            drawId: response.draw_id,
            drawNo: response.draw_no,
          });

          if (onDrawCreated) {
            onDrawCreated(response.draw_id);
          }
        } else {
          await apiService.updateDraw(drawData.drawId!, {
            draw_name: drawData.drawName,
            organisation: drawData.organisation,
            entry_method: drawData.entryMethod,
            prizes: drawData.prizes.map((prize) => ({
              description: prize.description,
              winners: prize.winners,
              reserves: prize.reserves || 0,
              estimated_value: prize.estimatedValue || 0,
            })),
          });
        }
      } catch (error) {
        if (isCreate && isMountedRef.current && !isAuthenticated) {
          setTurnstileToken(null);
          setTurnstileResetKey((n) => n + 1);
        }
        setIsSubmitting(false);
        return;
      } finally {
        if (isMountedRef.current) {
          setIsSubmitting(false);
        }
      }
    }

    onNext();
  };

  const turnstileRequired =
    !isLocked && !isAuthenticated && !!config.turnstile.siteKey;
  const turnstileSatisfied = !turnstileRequired || !!turnstileToken;

  const isFormValid =
    isValidDrawName(drawData.drawName) &&
    hasContent(drawData.organisation) &&
    turnstileSatisfied;

  const drawNameIsEmpty = !hasContent(drawData.drawName);
  const drawNameTooShort =
    hasContent(drawData.drawName) && !isValidDrawName(drawData.drawName);
  const drawNameHasError =
    showErrors && (drawNameIsEmpty || drawNameTooShort);
  const drawNameErrorMessage = drawNameIsEmpty
    ? "Enter a draw name to continue"
    : `Draw name must be at least ${MIN_DRAW_NAME_LENGTH} characters`;
  const organisationHasError = showErrors && !hasContent(drawData.organisation);

  return (
    <div>
      <WizardStepHeader
        title="Set up your draw"
        description="Two quick details and you're moving."
      />

      <div style={{ marginRight: "2rem" }}>
        <PlxsTextField
          dataTestId="draw-name-input"
          label="Draw name"
          value={drawData.drawName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            updateDrawData({ drawName: e.target.value });
          }}
          placeholder="e.g. Spring Customer Getaway"
          styleOverrides={{ hidePlaceholderEllipsis: true }}
          hasError={drawNameHasError}
          errorMessage={drawNameHasError ? drawNameErrorMessage : ""}
          isDisabled={isLocked}
          readOnly={isLocked}
        />
        <PlxsTextField
          dataTestId="organisation-input"
          label="Run by"
          value={drawData.organisation}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            updateDrawData({ organisation: e.target.value });
          }}
          placeholder="e.g. Acme Pty Ltd"
          styleOverrides={{ hidePlaceholderEllipsis: true }}
          hasError={organisationHasError}
          errorMessage={
            organisationHasError ? "Enter who's running this draw" : ""
          }
          isDisabled={isLocked}
          readOnly={isLocked}
        />
        {!isAuthenticated && !isLocked && config.turnstile.siteKey && (
          <TurnstileWidget
            key={turnstileResetKey}
            siteKey={config.turnstile.siteKey}
            onVerify={setTurnstileToken}
            onExpire={() => setTurnstileToken(null)}
            onError={() => setTurnstileToken(null)}
          />
        )}
      </div>

      <WizardFooterNav
        continueButton={{
          dataTestId: "next-draw-setup",
          label: "Next",
          variant: "primary",
          onClick: handleNext,
          isDisabled: isSubmitting || !turnstileSatisfied,
          isLoading: isSubmitting,
        }}
      />
    </div>
  );
};

export default DrawSetupStep;
